import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { lockScroll, onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

const headerMenuQuery = window.matchMedia('(min-width: 1201px)');

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  #scrollTop = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerdown', this.#onPointerDown, { capture: true });
    this.addEventListener('keyup', this.#onKeyUp);
    headerMenuQuery.addEventListener('change', this.#onBreakpointChange);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('pointerdown', this.#onPointerDown, { capture: true });
    this.removeEventListener('keyup', this.#onKeyUp);
    headerMenuQuery.removeEventListener('change', this.#onBreakpointChange);
    this.#resetHeaderSection();
  }

  #onBreakpointChange = (event) => {
    if (!event.matches || !this.isOpen) return;

    this.close();
  };

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  #onPointerDown = (event) => {
    if (this.isOpen || this.#getDetailsElement(event) !== this.refs.details) return;

    this.#saveScrollPosition();
    this.#prepareHeaderSection();
    lockScroll(this.refs.details);
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  #saveScrollPosition() {
    this.#scrollTop = document.scrollingElement?.scrollTop ?? window.scrollY;
  }

  #restoreScrollPosition() {
    if (this.#scrollTop === null || !document.scrollingElement) return;

    const scrollBehavior = document.scrollingElement.style.scrollBehavior;
    document.scrollingElement.style.scrollBehavior = 'auto';
    document.scrollingElement.scrollTop = this.#scrollTop;
    document.scrollingElement.style.scrollBehavior = scrollBehavior;
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');
    const isRootDrawer = details === this.refs.details;

    if (!summary) return;

    if (isRootDrawer) {
      if (this.#scrollTop === null) this.#saveScrollPosition();
      lockScroll(details);
    }

    this.#prepareHeaderSection();

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (isRootDrawer) this.#restoreScrollPosition();

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(
        drawer || details,
        () => {
          if (!details.open) return;

          trapFocus(details);
          if (isRootDrawer) this.#restoreScrollPosition();
        },
        { subtree: false }
      );
    });
  }

  #prepareHeaderSection() {
    const headerSection = this.closest('.header-section');
    if (headerSection instanceof HTMLElement) {
      const headerTop = Math.max(0, headerSection.getBoundingClientRect().top);
      headerSection.style.setProperty('--drawer-viewport-top', `${headerTop}px`);
      headerSection.classList.add('header-section--drawer-open');
    }
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');
    const isRootDrawer = details === this.refs.details;
    const scrollTop = isRootDrawer ? this.#scrollTop : null;

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (isRootDrawer) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
          this.#resetHeaderSection();
          requestAnimationFrame(() => {
            if (scrollTop !== null && document.scrollingElement) {
              this.#scrollTop = scrollTop;
              this.#restoreScrollPosition();
            }
            this.#scrollTop = null;
          });
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  #resetHeaderSection() {
    const headerSection = this.closest('.header-section');
    if (!(headerSection instanceof HTMLElement)) return;

    headerSection.classList.remove('header-section--drawer-open');
    headerSection.style.removeProperty('--drawer-viewport-top');
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
